/**
 * Shared pagination + sort primitives for list endpoints.
 *
 * Every paginated GET returns the same envelope so the console's table hook maps 1:1
 * across screens. Building the query-params object is centralised so every hook agrees
 * on parameter names (page / page_size / search / sort / dir), matching the backend
 * PageParams + SortParams shape.
 */

import type { TableQueryParams } from "@/lib/table"

/**
 * The largest page the backend will serve (schemas/pagination.MAX_PAGE_SIZE).
 * Asking for more is a 422, not a bigger page.
 */
export const MAX_PAGE_SIZE = 100

export interface PageWire<T> {
  items: T[]
  total: number
  page: number
  page_size: number
}

export interface Page<T> {
  items: T[]
  total: number
  page: number
  pageSize: number
}

export function fromPageWire<W, T>(wire: PageWire<W>, adapt: (w: W) => T): Page<T> {
  return {
    items: wire.items.map(adapt),
    total: wire.total,
    page: wire.page,
    pageSize: wire.page_size,
  }
}

/** One query-param value. An array is sent as the key repeated (`?id=1&id=2`), as `http` does. */
export type QueryParamValue = string | number | boolean | null | undefined | string[]

/**
 * Turn a table hook's query state into the query params the backend expects.
 * Callers may pass additional endpoint-specific params (e.g. `include_archived`) via
 * `extra`; they merge in on top so a filter can override a default.
 */
export function toQueryParams(
  params: TableQueryParams,
  extra?: Record<string, QueryParamValue>,
): Record<string, QueryParamValue> {
  const out: Record<string, QueryParamValue> = {
    page: params.page,
    page_size: params.pageSize,
  }
  if (params.search) out["search"] = params.search
  if (params.sort) {
    out["sort"] = params.sort.field
    out["dir"] = params.sort.dir
  }
  return { ...out, ...(extra ?? {}) }
}
